import React, { useState, useMemo } from "react";
import { useCollection, PROJECT_STATUS, fmtMoney, fmtDate } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, Pill, StatusDot, Labeled } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ChevronDown, ChevronRight, AlertTriangle, Trash2, Edit } from "lucide-react";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "active", label: "Active" }, { key: "red", label: "Red" }, { key: "yellow", label: "Yellow" },
  { key: "money", label: "Making Money" }, { key: "no_action", label: "No Next Action" }, { key: "deadline", label: "Deadline This Week" },
];
const PRIORITIES = ["low", "normal", "high", "critical"];

const empty = { name: "", category: "", leader: "", mission: "", status: "green", priority: "normal", stage: "", strategic_value: 3, revenue_potential: 0, capital_required: 0, progress: 0, next_action: "", next_milestone: "", deadline: "", notes: "" };

export default function Projects() {
  const { items, loading, create, update, remove } = useCollection("projects");
  const [filter, setFilter] = useState(null);
  const [openCats, setOpenCats] = useState({});
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const filtered = useMemo(() => {
    const wk = new Date(Date.now() + 7 * 864e5);
    return items.filter((p) => {
      if (!filter) return true;
      if (filter === "active") return ["green", "yellow", "red"].includes(p.status);
      if (filter === "red") return p.status === "red";
      if (filter === "yellow") return p.status === "yellow";
      if (filter === "money") return (p.revenue_potential || 0) > 0;
      if (filter === "no_action") return ["green", "yellow", "red"].includes(p.status) && !(p.next_action || "").trim();
      if (filter === "deadline") return p.deadline && new Date(p.deadline) <= wk;
      return true;
    });
  }, [items, filter]);

  const byCat = useMemo(() => {
    const g = {}; filtered.forEach((p) => { (g[p.category] = g[p.category] || []).push(p); }); return g;
  }, [filtered]);

  const openNew = () => { setForm(empty); setEditId(null); setDlg(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setDlg(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, strategic_value: Number(form.strategic_value) || 0, revenue_potential: Number(form.revenue_potential) || 0, capital_required: Number(form.capital_required) || 0, progress: Number(form.progress) || 0 };
    if (editId) await update(editId, payload); else await create(payload);
    toast.success("Saved"); setDlg(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader title="Projects" subtitle="The backbone of the empire" testId="projects-header"
        action={<button data-testid="project-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors"><Plus size={16} /> New</button>} />

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter(null)} className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border", !filter ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3]")}>All</button>
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`project-filter-${f.key}`} onClick={() => setFilter(f.key)} className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border", filter === f.key ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3]")}>{f.label}</button>
        ))}
      </div>

      {Object.keys(byCat).length === 0 ? <EmptyState title="No projects match this filter." /> : (
        <div className="space-y-3">
          {Object.entries(byCat).map(([cat, ps]) => {
            const isOpen = openCats[cat] ?? true;
            return (
              <div key={cat} className="bg-surface border border-edge rounded-xl overflow-hidden">
                <button data-testid={`project-cat-${cat}`} onClick={() => setOpenCats({ ...openCats, [cat]: !isOpen })} className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface2 transition-colors">
                  <div className="flex items-center gap-2">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}<span className="font-display font-bold">{cat}</span></div>
                  <span className="font-num text-xs text-[#A3A3A3]">{ps.length}</span>
                </button>
                {isOpen && (
                  <div className="divide-y divide-edge/50">
                    {ps.map((p) => {
                      const st = PROJECT_STATUS[p.status] || PROJECT_STATUS.green;
                      const noAction = ["green", "yellow", "red"].includes(p.status) && !(p.next_action || "").trim();
                      return (
                        <div key={p.id} data-testid={`project-${p.id}`} className="px-4 py-3 hover:bg-surface2 transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2"><StatusDot color={st.color} /><span className="font-semibold truncate">{p.name}</span>{p.priority === "critical" && <Pill color="#E11D48">critical</Pill>}{p.priority === "high" && <Pill color="#F59E0B">high</Pill>}</div>
                              {p.mission && <p className="text-sm text-[#A3A3A3] mt-1">{p.mission}</p>}
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                {p.leader && <Pill color="#A3A3A3">{p.leader}</Pill>}
                                {p.revenue_potential > 0 && <Pill color="#10B981">{fmtMoney(p.revenue_potential)}</Pill>}
                                {p.deadline && <Pill color="#F59E0B">{fmtDate(p.deadline)}</Pill>}
                              </div>
                              {p.progress > 0 && (
                                <div className="mt-2 max-w-[240px]">
                                  <div className="flex justify-between text-[0.6rem] label-caps mb-1"><span>Progress</span><span className="text-gold">{p.progress}%</span></div>
                                  <div className="h-2 rounded-full bg-edge overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, p.progress)}%`, background: "#D4AF37" }} /></div>
                                </div>
                              )}
                              {p.next_action ? <p className="text-sm mt-2"><span className="label-caps">Next: </span>{p.next_action}</p>
                                : noAction && <p className="text-sm mt-2 text-sred flex items-center gap-1.5"><AlertTriangle size={13} /> This project has no next move.</p>}
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <button data-testid={`project-edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={15} /></button>
                              <button onClick={async () => { await remove(p.id); toast.success("Archived"); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={15} /></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Name"><Input data-testid="project-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Leader"><Input value={form.leader} onChange={(e) => setForm({ ...form, leader: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Stage"><Input value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
              </Labeled>
              <Labeled label="Priority">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
              </Labeled>
              <Labeled label="Revenue Potential ($)"><Input type="number" value={form.revenue_potential} onChange={(e) => setForm({ ...form, revenue_potential: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Progress (%)"><Input data-testid="project-progress" type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Deadline"><Input type="date" value={(form.deadline || "").slice(0, 10)} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="Mission (one sentence)"><Input value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Next Action"><Input data-testid="project-next-action" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="project-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save Project</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
